import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { generateUUID } from '@repo/lib/crypto';
import { type ToastPosition, type ToastProps } from '@repo/ui/Toast';
import { ToastGroup } from '@repo/ui/Toast/ToastGroup';
import { ToastRecipeContent } from '@repo/ui/Toast/ToastRecipeContent';

export type ToastItem = ToastProps & { id: string };
export type ToastRecipe = Omit<
  ToastProps,
  'children' | 'variant' | 'onClose'
> & {
  message: string;
};

interface ToastContextType {
  showToast: (props: Omit<ToastProps, 'onClose'>) => string;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Global singleton to store toast functions
const toastSingleton: {
  showToast: ((props: Omit<ToastProps, 'onClose'>) => string) | undefined;
  hideToast: ((id: string) => void) | undefined;
  isInitialized: boolean;
  subscribers: (() => void)[];
} = {
  showToast: undefined,
  hideToast: undefined,
  isInitialized: false,
  subscribers: [],
};

// Register a provider to make it the active toast provider
const registerProvider = (
  showToast: (props: Omit<ToastProps, 'onClose'>) => string,
  hideToast: (id: string) => void,
) => {
  toastSingleton.showToast = showToast;
  toastSingleton.hideToast = hideToast;
  toastSingleton.isInitialized = true;

  // Notify subscribers that a provider is available
  toastSingleton.subscribers.forEach((callback) => callback());
  return () => {
    // Only unregister if this is still the current provider
    if (toastSingleton.showToast === showToast) {
      toastSingleton.isInitialized = false;
    }
  };
};

export const ToastRoot = ({
  children,
  config,
}: {
  children?: ReactNode;
  // Optional config for advanced use cases
  config?: {
    maxToasts?: number;
    defaultPosition?: ToastPosition;
    defaultDuration?: number;
  };
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const configRef = useRef(config ?? {});

  const showToast = useCallback((props: Omit<ToastProps, 'onClose'>) => {
    const id = generateUUID();

    // Apply default configuration if provided
    const toastWithDefaults = {
      ...props,
      position:
        props.position ?? configRef.current.defaultPosition ?? 'bottomRight',
      duration: props.duration ?? configRef.current.defaultDuration ?? 3000,
    };

    setToasts((prev) => {
      const maxToasts = configRef.current.maxToasts ?? 3;
      // If maxToasts is set, remove oldest toasts when limit is reached
      if (maxToasts && prev.length >= maxToasts) {
        return [
          ...prev.slice(prev.length - maxToasts + 1),
          { ...toastWithDefaults, id },
        ];
      }
      return [...prev, { ...toastWithDefaults, id }];
    });

    return id;
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Register this provider with the singleton when mounted
  useEffect(() => {
    const unregister = registerProvider(showToast, hideToast);
    return unregister;
  }, [showToast, hideToast]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ showToast, hideToast }),
    [showToast, hideToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastGroup toasts={toasts} hideToast={hideToast} />
    </ToastContext.Provider>
  );
};

export const toast = {
  show: (options: Omit<ToastProps, 'onClose'>) => {
    if (!toastSingleton.showToast) {
      // eslint-disable-next-line no-console
      console.warn(
        'Toast system not initialized. Add <ToastProvider /> to your app.',
      );

      // Return a promise that resolves when a provider becomes available
      return new Promise<string>((resolve) => {
        const checkForProvider = () => {
          if (toastSingleton.showToast) {
            resolve(toastSingleton.showToast({ ...options }));
          }
        };

        // Add to subscribers to be notified when a provider is available
        toastSingleton.subscribers.push(checkForProvider);
      });
    }
    return toastSingleton.showToast({ ...options });
  },
  error: ({ message, ...options }: ToastRecipe) =>
    toast.show({
      children: <ToastRecipeContent message={message} icon="alert" />,
      variant: 'error',
      ...options,
    }),
  info: ({ message, ...options }: ToastRecipe) =>
    toast.show({
      children: <ToastRecipeContent message={message} icon="info-circle" />,
      variant: 'info',
      ...options,
    }),
  success: ({ message, ...options }: ToastRecipe) =>
    toast.show({
      children: <ToastRecipeContent message={message} icon="check" />,
      variant: 'success',
      ...options,
    }),
  hide: (id: string) => {
    if (!toastSingleton.hideToast) {
      // eslint-disable-next-line no-console
      console.warn(
        'Toast system not initialized. Add <ToastProvider /> to your app.',
      );
      return;
    }
    return toastSingleton.hideToast(id);
  },
  isReady: () => toastSingleton.isInitialized,
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    if (!toastSingleton.isInitialized) {
      throw new Error(
        'useToast must be used within a ToastProvider or after a ToastProvider is mounted elsewhere',
      );
    }

    // Fall back to the singleton if no context is available
    return {
      toast: toast.show,
      errorToast: toast.error,
      infoToast: toast.info,
      successToast: toast.success,
      hideToast: toast.hide,
    };
  }

  const { showToast, hideToast } = context;

  return {
    toast: showToast,
    errorToast: ({ message, ...options }: ToastRecipe) => {
      return showToast({
        children: <ToastRecipeContent message={message} icon="alert" />,
        variant: 'error',
        ...options,
      });
    },
    infoToast: ({ message, ...options }: ToastRecipe) => {
      return showToast({
        children: <ToastRecipeContent message={message} icon="info-circle" />,
        variant: 'info',
        ...options,
      });
    },
    successToast: ({ message, ...options }: ToastRecipe) => {
      return showToast({
        children: <ToastRecipeContent message={message} icon="check" />,
        variant: 'success',
        ...options,
      });
    },
    hideToast,
  };
};
